import { Button } from '@monksflow/monks-ui';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';
import { getPersonaFetchErrorMessage } from '@/entities/persona';
import { EmptyState } from '@/shared/ui/EmptyState';
import { AudiencesGridV2 } from '../AudiencesGridV2';
import { AudiencesTableV2 } from '../AudiencesTableV2';
import { AudiencesToolbarV2 } from '../AudiencesToolbarV2';
import { useAudiencesListV2, useAudienceViewMode } from './hooks';
import type { AudiencesContentV2Props } from './AudiencesContentV2.types';

export function AudiencesContentV2({
  personas,
  isPending,
  isError = false,
  fetchError,
  refetch,
  onPersonaClick,
  onCreateClick,
  onEditPersona,
  onDeletePersona,
}: AudiencesContentV2Props) {
  const { t } = useTranslation('audiences');

  const gwiEnabled = useBooleanFlagValue('gwi_audience_linking', false);
  const { viewMode, setViewMode } = useAudienceViewMode();
  const list = useAudiencesListV2({ personas });

  const hasPersonas = (personas?.length ?? 0) > 0;
  // A failed background refetch keeps the rows on screen; only a cold failure takes over.
  const showError = isError && !hasPersonas;
  const showInitialEmpty = !isPending && !isError && !hasPersonas;
  const isFiltered = list.debouncedSearch.trim().length > 0 || list.selectedStatuses.length > 0;
  const showNoResults = hasPersonas && list.visiblePersonas.length === 0 && isFiltered;
  // With nothing to search or filter there is nothing for the toolbar to do.
  const showToolbar = !showInitialEmpty && !showError;

  if (showError) {
    return (
      <EmptyState
        title={t('audiences.audiencesContentV2.couldntLoad')}
        description={getPersonaFetchErrorMessage(fetchError)}
        data-testid="audiences-error-state"
      >
        <Button variant="outline" onClick={refetch}>
          {t('audiences.audiencesContentV2.tryAgain')}
        </Button>
      </EmptyState>
    );
  }

  if (showInitialEmpty) {
    return (
      <EmptyState
        title={t('audiences.audiencesContentV2.emptyTitle')}
        description={t('audiences.audiencesContentV2.emptyDescription')}
        data-testid="audiences-empty-state"
      >
        {onCreateClick && (
          <Button onClick={onCreateClick}>
            {t('audiences.audiencesContentV2.createAudience')}
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showToolbar && (
        <AudiencesToolbarV2
          search={list.searchInput}
          onSearchChange={list.setSearchInput}
          selectedStatuses={list.selectedStatuses}
          onSelectedStatusesChange={list.setSelectedStatuses}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onCreateClick={onCreateClick}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
        {showNoResults ? (
          <EmptyState
            title={t('audiences.audiencesContentV2.noResultsTitle')}
            description={t('audiences.audiencesContentV2.noResultsDescription')}
            data-testid="audiences-no-results-state"
          />
        ) : viewMode === 'grid' ? (
          <AudiencesGridV2
            personas={list.visiblePersonas}
            isLoading={isPending}
            gwiEnabled={gwiEnabled}
            onPersonaClick={onPersonaClick}
            onEdit={onEditPersona}
            onDelete={onDeletePersona}
          />
        ) : (
          <AudiencesTableV2
            personas={list.visiblePersonas}
            gwiEnabled={gwiEnabled}
            isLoading={isPending}
            sortBy={list.sortBy}
            sortDirection={list.sortDirection}
            onSort={list.handleSort}
            onEdit={onEditPersona}
            onDelete={onDeletePersona}
          />
        )}
      </div>
    </div>
  );
}

AudiencesContentV2.displayName = 'AudiencesContentV2';
